import { useState, useEffect, useCallback, useMemo } from 'react'
import { useParams, Link, useNavigate } from 'react-router-dom'
import * as Sentry from '@sentry/react'
import {
  useECustoms, type EcustomsPreview, type EcustomsChecklistItem, type EcustomsSubmission,
  type EcustomsGenerateV2Result, type PvaMode, type DanMode, type DeclType, type LoadType, type V2Decisions, type PrecheckResult,
} from '../../hooks/useECustoms'
import SubmitConfirmDialog from '../../components/SubmitConfirmDialog'
import PreSubmitChecklist from '../../components/ecustoms/PreSubmitChecklist'
import SubmissionHistory from '../../components/ecustoms/SubmissionHistory'
import { checklistStatus } from '../../lib/precheckStatus'
import { useEcustomsSettings } from '../../hooks/useEcustomsSettings'
import PoaStatusBadge from '../../components/PoaStatusBadge'
import { ISO_COUNTRIES } from '../../lib/cds/countries'
import RawCsvEditor from '../../components/RawCsvEditor'

// Z73.5 / Z73.8: client-side CSV download -- the CSVs already come back in full from the server
// (either preview or generate), no extra round-trip needed to hand them to Gary's machine.
// Z73.8: console.log at each step per Gary -- Bug 3 (toast said "success" but no file arrived)
// turned out to be the edge function not having redeployed after earlier changes, so the OLD
// deployed 'generate' handler was silently falling through to its 'preview' branch instead
// (different response shape, no top-level pva/nonPva.csvContent) -- these logs make that
// class of mismatch visible in DevTools immediately instead of only inferable from a missing file.
function downloadCsv(filename: string, content: string) {
  console.log('[eCustoms] downloadCsv: triggering download', { filename, contentLength: content.length })
  if (!content) {
    console.error('[eCustoms] downloadCsv: content is empty -- aborting, nothing to download for', filename)
    return
  }
  const blob = new Blob([content], { type: 'text/csv;charset=utf-8;' })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
  URL.revokeObjectURL(url)
  console.log('[eCustoms] downloadCsv: download triggered for', filename)
}

function formatSubmissionStatus(status: string): string {
  if (status === 'downloaded_for_manual_test') return 'Downloaded (manual UAT test, pre-Z73.7)'
  if (status === 'generated') return 'Generated & downloaded'
  return status
}

// Z73.7: a real CDS line can't be missing these -- mirrors REQUIRED_ROW_FIELDS in
// submit-ecustoms/index.ts exactly (the server re-checks the same list independently; this copy
// only drives the red-highlight, never the actual gate).
const REQUIRED_FIELDS = new Set(['GOODS DESCRIPTION', 'COMMODITY CODE', 'VALUE', 'GROSS MASS', 'NET MASS', 'ORIGIN CNTRY', 'PKG QTY'])

// Z73.11: not strictly required (an empty cell here doesn't block Confirm & Generate), but
// worth a "you probably want a value here" nudge -- the bottom counter's "warning cells" and the
// grid's amber empty-cell highlight both come from this list. Deliberately doesn't include the
// DAN-related DOC CODE/STATUS/REF columns -- those are genuinely optional for most declarations
// (only relevant with a specific duty deferment account), so leaving them blank is normal, not a
// gap worth flagging on every row.
const RECOMMENDED_FIELDS = new Set(['CPC FIRST 4', 'CPC ADDITIONAL', 'TAX MOP 1', 'TAX MOP 2'])

// Z73.11 per Gary: known-value columns get a <datalist> of common values -- staff can still type
// anything, this is a suggestion, not a restriction. ORIGIN CNTRY reuses the same ISO country
// list used everywhere else in this app (src/lib/cds/countries.ts) rather than a separate list.
const COLUMN_SUGGESTIONS: Record<string, string[]> = {
  'CPC FIRST 4': ['4000', '6123'],
  'CPC ADDITIONAL': ['000', '001', '003', 'F01'],
  'DOC CODE 1': ['N935', 'C644', 'Y929', 'N851'],
  'DOC CODE 2': ['N935', 'C644', 'Y929', 'N851'],
  'DOC CODE 3': ['N935', 'C644', 'Y929', 'N851'],
  'TAX MOP 1': ['A', 'B', 'E', 'F', 'G'],
  'TAX MOP 2': ['A', 'B', 'E', 'F', 'G'],
  'PKG TYPE': ['PK', 'CT', 'CS', 'PL', 'BX'],
  'ORIGIN CNTRY': ISO_COUNTRIES.map(c => c.code),
}

// Z73.11: plain-English meaning + an example, for the "Column Legend" panel -- grounded in what
// this codebase's own CDS logic actually does (see submit-ecustoms/index.ts's buildCdsRows,
// dutyMop/vatMop), not guessed. TAX MOP's A/B/F values are real CDS codes Gary asked to offer as
// suggestions but aren't produced by our own auto-fill (which only ever writes C/E/G) -- flagged
// as such rather than inventing a meaning for them.
const COLUMN_LEGEND: Record<string, { meaning: string; example: string }> = {
  'GOODS DESCRIPTION': { meaning: 'Plain-text description of the goods on this line.', example: 'Glass bottle' },
  'GROSS MASS': { meaning: 'Total weight including packaging, in kg.', example: '361.70' },
  'NET MASS': { meaning: 'Weight of the goods alone (no packaging), in kg.', example: '340.70' },
  'VALUE': { meaning: 'Customs value of this line, in VALUE CURR.', example: '79.80' },
  'VALUE CURR': { meaning: 'Currency VALUE is expressed in.', example: 'USD' },
  'COMMODITY CODE': { meaning: 'HS / commodity tariff code classifying the goods.', example: '7020008000' },
  'DOC REF 1': { meaning: "Reference for this line's supporting document -- normally the invoice number.", example: '2026-MF062603' },
  'DOC REASON 1': { meaning: 'Reason code for DOC REF 1, when required. Usually left blank.', example: '(blank)' },
  'PREV DOC REF 1': { meaning: 'Reference to the previous customs document -- usually the same invoice number.', example: '2026-MF062603' },
  'PKG QTY': { meaning: 'Number of packages/cartons for this line.', example: '21' },
  'PKG TYPE': { meaning: 'Package type code.', example: 'CT (Carton)' },
  'PKG MARKS': { meaning: 'Shipping marks/numbers on the packages, or N/M if none.', example: 'N/M' },
  'SUPP UNITS': { meaning: 'Supplementary units (e.g. piece count), when the commodity code requires them.', example: '7980' },
  'SUPP UNITS CODE': { meaning: 'Unit-of-measure code for SUPP UNITS. Usually left blank unless the commodity code requires a specific unit.', example: '(blank)' },
  'VALUATION METHOD': { meaning: 'Customs valuation method code -- 1 is Transaction Value, the standard method.', example: '1' },
  'CPC FIRST 4': { meaning: 'First 4 digits of the Customs Procedure Code -- 4000 is a standard free-circulation import.', example: '4000' },
  'CPC ADDITIONAL': { meaning: '3-character CPC suffix -- 000 means no additional procedure.', example: '000' },
  'ORIGIN CNTRY': { meaning: '2-letter ISO country of origin of the goods.', example: 'CN' },
  'PREFERENCE': { meaning: 'Tariff preference code -- 100 means no preference claimed.', example: '100' },
  'DOC CODE 1': { meaning: 'Code for a supporting document on this line (e.g. an origin declaration). Only usually needed alongside a duty deferment account (DAN).', example: 'N935' },
  'DOC STATUS 1': { meaning: 'Status code for the matching DOC CODE (e.g. AC = Attached/Certified).', example: 'AC' },
  'DOC CODE 2': { meaning: 'Second supporting-document code, same idea as DOC CODE 1 -- this repo auto-fills a guarantee-waiver document here for a CY UK DAN job.', example: 'C505' },
  'DOC STATUS 2': { meaning: 'Status code for DOC CODE 2.', example: 'CC' },
  'DOC REF 2': { meaning: 'Reference for DOC CODE 2 -- e.g. a guarantee reference.', example: 'GBCGUGUARANTEENOTREQUIRED' },
  'DOC CODE 3': { meaning: 'Third supporting-document code -- this repo auto-fills a DAN reference document here for a CY UK DAN job.', example: 'C506' },
  'DOC STATUS 3': { meaning: 'Status code for DOC CODE 3. Usually left blank.', example: '(blank)' },
  'DOC REF 3': { meaning: 'Reference for DOC CODE 3 -- e.g. the DAN number.', example: 'GBDPO6150757' },
  'PREV DOC CLASS 1': { meaning: 'Class of the previous document referenced -- Z means a commercial document.', example: 'Z' },
  'PREV DOC TYPE 1': { meaning: 'Type code of the previous document -- 380 is a commercial invoice.', example: '380' },
  'TAX TYPE 1': { meaning: 'Which tax this line pays -- A00 is Customs Duty.', example: 'A00' },
  'TAX MOP 1': { meaning: "Method of payment for TAX TYPE 1's duty. This repo auto-fills E (deferment account) or C (cash), depending on whether the job uses CY UK's own DAN. A/B/F are other valid CDS codes not produced by our auto-fill.", example: 'E' },
  'TAX TYPE 2': { meaning: 'Which tax this line pays -- B00 is Import VAT.', example: 'B00' },
  'TAX MOP 2': { meaning: "Method of payment for TAX TYPE 2's VAT. This repo auto-fills G for Postponed VAT Accounting, otherwise the same as TAX MOP 1. A/B/F are other valid CDS codes not produced by our auto-fill.", example: 'G' },
}

// Z73.7: the combined client-side row shape -- cells plus which invoice_items rows they came
// from, travelling together through add/remove/edit so "save edits back to source" never
// misattributes one row's edits to a different row after the grid's been rearranged.
interface GridRow {
  cells: Record<string, string>
  itemIds: string[]
}

function cloneRows(rows: Record<string, string>[] | null, itemIds: string[][]): GridRow[] {
  if (!rows) return []
  return rows.map((cells, i) => ({ cells: cleanCells(cells), itemIds: itemIds[i] ? [...itemIds[i]] : [] }))
}

function emptyRow(headers: string[]): GridRow {
  return { cells: Object.fromEntries(headers.map(h => [h, ''])), itemIds: [] }
}

// Z92: integer-unit summing (cents / grams) -- plain float addition shows 15646.699999999997.
function sumField(rows: GridRow[], field: string): number {
  const dp = field === 'VALUE' ? 2 : 3
  return rows.reduce((sum, r) => sum + Math.round((parseFloat(r.cells[field]) || 0) * 10 ** dp), 0) / 10 ** dp
}

// Z92: values already saved/generated with float noise (e.g. 3435.7200000000003) are cleaned on load.
function cleanCells(cells: Record<string, string>): Record<string, string> {
  const out = { ...cells }
  const clean = (k: string, dp: number) => {
    const n = parseFloat(out[k])
    if (out[k] !== undefined && out[k] !== '' && Number.isFinite(n)) {
      out[k] = dp === 2 ? (Math.round(n * 100) / 100).toFixed(2) : String(Math.round(n * 1000) / 1000)
    }
  }
  clean('VALUE', 2); clean('GROSS MASS', 3); clean('NET MASS', 3)
  return out
}

type Tab = 'pva' | 'nonPva' | 'rawCsv'

function ChecklistPanel({ checklist }: { checklist: EcustomsChecklistItem[] }) {
  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6 mb-6">
      <h2 className="text-base font-semibold text-gray-900 dark:text-white mb-3">Validation Checklist</h2>
      <ul className="space-y-2">
        {checklist.map(item => {
          // Z73.6 revised: an unpassed "warning" item (POA expiring soon, borderline name match)
          // is amber, not red -- it's surfaced but doesn't block. Z73.6.1's POA Status line is
          // shown even when passed (its detail carries the 🟢 ACTIVE confirmation too), unlike
          // every other item here, which only ever sets detail on failure.
          const isWarningOnly = !item.passed && item.severity === 'warning'
          const iconColor = item.passed ? 'text-green-600 dark:text-green-400' : isWarningOnly ? 'text-amber-600 dark:text-amber-400' : 'text-red-600 dark:text-red-400'
          const labelColor = item.passed ? 'text-gray-700 dark:text-gray-300' : isWarningOnly ? 'text-amber-700 dark:text-amber-400 font-medium' : 'text-red-700 dark:text-red-400 font-medium'
          return (
            <li key={item.key} className="flex items-start gap-2 text-sm">
              <span className={iconColor}>{item.passed ? '✓' : isWarningOnly ? '⚠' : '✗'}</span>
              <div>
                <span className={labelColor}>{item.label}</span>
                {item.detail && <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{item.detail}</p>}
              </div>
            </li>
          )
        })}
      </ul>
    </div>
  )
}

// Z73.7: the editable preview grid -- one row per CDS declaration line, one input per field.
// Missing required cells are highlighted red; a cell that's been edited away from its source
// value is highlighted amber with a "reset to source" button. "Excel-like" here means inline
// <input>s in a scrollable table (same pattern SpreadsheetPreview.tsx already uses elsewhere in
// this app for wide, many-column grids) rather than a literal spreadsheet component.
function EditableGrid({
  headers, rows, sourceRows, onCellChange, onResetCell, onRemoveRow, onSetAllRows,
}: {
  headers: string[]
  rows: GridRow[]
  sourceRows: GridRow[]
  onCellChange: (rowIdx: number, field: string, value: string) => void
  onResetCell: (rowIdx: number, field: string) => void
  onRemoveRow: (rowIdx: number) => void
  onSetAllRows: (field: string, value: string) => void
}) {
  if (rows.length === 0) {
    return <div className="p-6 text-sm text-gray-500 dark:text-gray-400 italic">No line items -- use "+ Add Row" below, or this variant is N/A for this job.</div>
  }
  return (
    <div className="overflow-x-auto max-h-[32rem]">
      <table className="text-xs border-collapse w-full">
        <thead>
          <tr>
            <th className="sticky top-0 left-0 z-20 bg-gray-100 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 px-2 py-1 w-8">#</th>
            {headers.map(h => (
              <th key={h} className="sticky top-0 z-10 bg-gray-100 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 px-2 py-1 font-mono font-normal text-gray-500 dark:text-gray-400 whitespace-nowrap">
                <div className="flex items-center gap-1">
                  <span>{h}</span>
                  {rows.length > 1 && (
                    <button
                      onClick={() => { const v = rows[0]?.cells[h] ?? ''; onSetAllRows(h, v) }}
                      title={`Set every row's ${h} to row 1's value ("${rows[0]?.cells[h] || ''}")`}
                      className="text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 font-sans normal-case"
                    >
                      ⇊
                    </button>
                  )}
                </div>
              </th>
            ))}
            <th className="sticky top-0 z-10 bg-gray-100 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 px-2 py-1 w-8" />
          </tr>
        </thead>
        <tbody>
          {rows.map((row, i) => (
            <tr key={i}>
              <td className="sticky left-0 z-10 bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 px-2 py-1 text-gray-400 text-right font-mono">{i + 1}</td>
              {headers.map(h => {
                const value = row.cells[h] ?? ''
                const sourceValue = sourceRows[i]?.cells?.[h]
                const isMissing = REQUIRED_FIELDS.has(h) && !value.trim()
                const isEdited = sourceValue !== undefined && sourceValue !== value
                // Z73.11: an auto-mapped cell (sourceRows had a real value here, before any
                // edits) gets a grey background -- "the system provided this" -- distinct from a
                // recommended-but-blank cell (amber) or a required-and-blank one (red).
                const isAutoMapped = !!sourceValue?.trim() && !isEdited
                const isRecommendedEmpty = !isMissing && RECOMMENDED_FIELDS.has(h) && !value.trim()
                const bg = isMissing ? 'bg-red-50 dark:bg-red-900/30'
                  : isEdited ? 'bg-amber-50 dark:bg-amber-900/20'
                  : isRecommendedEmpty ? 'bg-amber-50/60 dark:bg-amber-900/10'
                  : isAutoMapped ? 'bg-gray-100 dark:bg-gray-700/40'
                  : ''
                const suggestions = COLUMN_SUGGESTIONS[h]
                return (
                  <td key={h} className={`border border-gray-200 dark:border-gray-700 p-0.5 ${bg}`}>
                    <div className="flex items-center gap-0.5">
                      <input
                        value={value}
                        onChange={e => onCellChange(i, h, e.target.value)}
                        list={suggestions ? `dl-${h.replace(/\s+/g, '-')}` : undefined}
                        title={isEdited ? `Source: ${sourceValue || '(empty)'}` : isMissing ? 'Required by Descartes -- please fill' : isRecommendedEmpty ? 'Usually has a value -- worth filling in' : undefined}
                        className="w-20 px-1 py-0.5 bg-transparent text-gray-800 dark:text-gray-200 focus:outline-none focus:ring-1 focus:ring-blue-500 rounded-sm"
                      />
                      {isEdited && (
                        <button
                          onClick={() => onResetCell(i, h)}
                          title={`Reset to source: ${sourceValue || '(empty)'}`}
                          className="text-blue-500 hover:text-blue-700 dark:text-blue-400 flex-shrink-0"
                        >
                          ↺
                        </button>
                      )}
                    </div>
                  </td>
                )
              })}
              <td className="border border-gray-200 dark:border-gray-700 px-1 py-0.5 text-center">
                <button onClick={() => onRemoveRow(i)} title="Remove row" className="text-red-500 hover:text-red-700">✕</button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {/* Z73.11: one <datalist> per known-value column, shared by every row's <input list="...">
          for that column -- staff can still type anything, this only suggests. */}
      {headers.filter(h => COLUMN_SUGGESTIONS[h]).map(h => (
        <datalist key={h} id={`dl-${h.replace(/\s+/g, '-')}`}>
          {COLUMN_SUGGESTIONS[h].map(v => <option key={v} value={v} />)}
        </datalist>
      ))}
    </div>
  )
}

// Z73.11: collapsible reference table so junior staff can learn what each CDS column means
// without asking every time -- grounded in this repo's own field-mapping logic, not guessed.
function ColumnLegendPanel({ headers }: { headers: string[] }) {
  const [open, setOpen] = useState(false)
  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 mb-6 overflow-hidden">
      <button
        onClick={() => setOpen(o => !o)}
        className="w-full flex items-center justify-between px-4 py-3 text-sm font-semibold text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700/50"
      >
        <span>📖 Column Legend -- what each field means</span>
        <span>{open ? '▲' : '▼'}</span>
      </button>
      {open && (
        <div className="max-h-96 overflow-y-auto border-t border-gray-200 dark:border-gray-700">
          <table className="w-full text-xs">
            <thead className="bg-gray-50 dark:bg-gray-700 sticky top-0">
              <tr>
                <th className="text-left px-3 py-2 font-semibold text-gray-600 dark:text-gray-300">Column</th>
                <th className="text-left px-3 py-2 font-semibold text-gray-600 dark:text-gray-300">Meaning</th>
                <th className="text-left px-3 py-2 font-semibold text-gray-600 dark:text-gray-300">Example</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
              {headers.map(h => (
                <tr key={h}>
                  <td className="px-3 py-2 font-mono text-gray-900 dark:text-white whitespace-nowrap">{h}</td>
                  <td className="px-3 py-2 text-gray-600 dark:text-gray-300">{COLUMN_LEGEND[h]?.meaning || '—'}</td>
                  <td className="px-3 py-2 font-mono text-gray-500 dark:text-gray-400 whitespace-nowrap">{COLUMN_LEGEND[h]?.example || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}

export default function ECustomsPreview() {
  const { id } = useParams<{ id: string }>()
  // Z73.4: previewLoading and submitting are separate flags now -- see useECustoms.ts's header
  // comment for why sharing one flag made the Submit button show "Submitting…" on page load,
  // before anyone clicked anything.
  const {
    previewLoading, submitting, generating, generatingV2, previewDeclaration, submitDeclarationV2,
    generateDeclaration, getSubmissionHistory, findPreviousBatch, generateInvoiceDraft,
    generateDeclarationV2, precheckV2, getSubmissionArchive,
  } = useECustoms()
  const navigate = useNavigate()
  const [invoiceDraftLoading, setInvoiceDraftLoading] = useState(false)
  const [invoiceDraftError, setInvoiceDraftError] = useState<string | null>(null)
  const [v2Error, setV2Error] = useState<string | null>(null)
  // Z73.22: kept around (not just downloaded-and-discarded) so the Raw CSV Editor tab has
  // something to render -- generate_v2's structuredRows.
  const [v2Result, setV2Result] = useState<EcustomsGenerateV2Result | null>(null)
  // Z92: VAT treatment is chosen explicitly for every declaration (null = not chosen yet). It is NEVER
  // pre-filled from the customer profile (that flag was bulk-seeded and is not trustworthy).
  const [pvaMode, setPvaMode] = useState<PvaMode | null>(null)
  const [confirmOpen, setConfirmOpen] = useState(false)
  // Pre-submit checklist (PR-C). Every decision is explicit (null = not chosen); defaults come from the customer
  // record / the job and are shown with their source. Overrides apply to this declaration only, never written back.
  const [danInput, setDanInput] = useState<DanMode | null>(null)
  const [declType, setDeclType] = useState<DeclType | null>(null)
  const [repInput, setRepInput] = useState<'2' | '3' | null>(null)
  const [loadInput, setLoadInput] = useState<LoadType | null>(null)
  const [arrivalIdInput, setArrivalIdInput] = useState<string | null>(null)
  const [mrnInput, setMrnInput] = useState('')
  const [vatOverride, setVatOverride] = useState<number | null>(null)
  const [ticks, setTicks] = useState<Record<string, string>>({})
  const [precheck, setPrecheck] = useState<PrecheckResult | null>(null)
  const [precheckLoading, setPrecheckLoading] = useState(false)
  const { uatVerified } = useEcustomsSettings()
  const [preview, setPreview] = useState<EcustomsPreview | null>(null)
  const arrivalId = (arrivalIdInput ?? preview?.job.inland_transport_id ?? '').trim().toUpperCase().replace(/\s+/g, ' ')
  const danMode: DanMode | null = danInput ?? (preview?.job.dan_default === true ? 'DAN' : preview?.job.dan_default === false ? 'NO_DAN' : null)
  const declarantRep = repInput ?? precheck?.defaults?.declarantRep ?? null
  const loadType: LoadType | null = loadInput ?? precheck?.defaults?.loadType ?? null
  const decisions: V2Decisions = useMemo(() => ({
    pvaMode, danMode, declType, declarantRep, loadType, arrivalId, transitMrn: mrnInput.trim().toUpperCase(), vatAdjOverride: vatOverride,
  }), [pvaMode, danMode, declType, declarantRep, loadType, arrivalId, mrnInput, vatOverride])
  const decisionsKey = JSON.stringify(decisions)
  const checklist = checklistStatus(decisions, precheck, ticks, preview?.job.transport_mode)
  const checklistOk = checklist.complete
  const checklistHint = `Complete the checklist first (${checklist.remaining} to go)`
  // The generated file was built from different decisions than the ones now on screen -> unusable.
  const fileStale = !!v2Result && (!v2Result.decisions || JSON.stringify([
    v2Result.decisions.pvaMode, v2Result.decisions.danMode, v2Result.decisions.declType, v2Result.decisions.declarantRep, v2Result.decisions.loadType,
    v2Result.decisions.arrivalId, v2Result.decisions.transitMrn || '', v2Result.decisions.vatAdjOverride,
  ]) !== JSON.stringify([decisions.pvaMode, decisions.danMode, decisions.declType, decisions.declarantRep, decisions.loadType, decisions.arrivalId, decisions.transitMrn, decisions.vatAdjOverride]))
  // Dry-run build of the real file whenever a decision changes: the review values and checks are READ FROM THAT FILE.
  useEffect(() => {
    if (!id || !preview) return
    let cancelled = false
    setPrecheckLoading(true)
    const t = setTimeout(() => {
      precheckV2(id, decisions)
        .then(r => { if (!cancelled) setPrecheck(r) })
        .catch(e => { if (!cancelled) setPrecheck({ success: false, error: e instanceof Error ? e.message : String(e), blocker: e instanceof Error ? e.message : String(e), review: [], checks: [], missing: [] }) })
        .finally(() => { if (!cancelled) setPrecheckLoading(false) })
    }, 350)
    return () => { cancelled = true; clearTimeout(t) }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, preview, decisionsKey])
  const [previewError, setPreviewError] = useState<string | null>(null)
  const [tab, setTab] = useState<Tab>('pva')
  const [submitMessages, setSubmitMessages] = useState<{ pva: string; nonPva: string } | null>(null)
  const [submitSucceeded, setSubmitSucceeded] = useState(false)
  const [history, setHistory] = useState<EcustomsSubmission[]>([])
  const [historyError, setHistoryError] = useState<string | null>(null)

  // Z73.7: the editable grid's working state, per variant -- source* is the immutable
  // as-generated copy used for diffing/reset, pvaRows/nonPvaRows is what staff actually edits.
  const [sourcePvaRows, setSourcePvaRows] = useState<GridRow[]>([])
  const [sourceNonPvaRows, setSourceNonPvaRows] = useState<GridRow[]>([])
  const [pvaRows, setPvaRows] = useState<GridRow[]>([])
  const [nonPvaRows, setNonPvaRows] = useState<GridRow[]>([])
  const [overrideScope, setOverrideScope] = useState<'submission_only' | 'saved_to_source'>('submission_only')
  const [generateError, setGenerateError] = useState<string | null>(null)
  const [rowErrors, setRowErrors] = useState<{ variant: 'pva' | 'nonPva'; row: number; field: string }[] | null>(null)
  const [generateSucceeded, setGenerateSucceeded] = useState(false)
  const [generateMessage, setGenerateMessage] = useState<string | null>(null)
  const [previousBatch, setPreviousBatch] = useState<{ batchId: string; jobRef: string; pvaRows: Record<string, string>[] | null; nonPvaRows: Record<string, string>[] | null } | null>(null)

  const loadHistory = useCallback(async () => {
    if (!id) return
    try {
      setHistory(await getSubmissionHistory(id))
      setHistoryError(null)
    } catch (e) {
      setHistoryError(e instanceof Error ? e.message : String(e))   // never let the history silently vanish
    }
  }, [id, getSubmissionHistory])

  // Z73.16 Part 4: the most recently generated declaration batch (pva + non-pva rows share a
  // batch_id) and whether it already has an invoice -- drives the "Create Invoice Draft" panel.
  const latestGeneratedBatch = useMemo(() => {
    const generated = history.filter(h => h.status === 'generated' && h.batch_id)
    if (generated.length === 0) return null
    const batchId = generated[0].batch_id
    const rows = generated.filter(h => h.batch_id === batchId)
    const invoiced = rows.find(r => r.invoice_id)
    return { batchId, invoiceId: invoiced?.invoice_id ?? null, invoiceNumber: invoiced?.invoice?.[0]?.invoice_number ?? null }
  }, [history])

  const handleCreateInvoiceDraft = async () => {
    if (!id) return
    setInvoiceDraftLoading(true)
    setInvoiceDraftError(null)
    try {
      const result = await generateInvoiceDraft(id)
      if (!result.success || !result.invoiceId) {
        setInvoiceDraftError(result.error || 'Failed to create invoice draft')
        return
      }
      await loadHistory()
      navigate(`/accounts?invoice=${result.invoiceId}`)
    } finally {
      setInvoiceDraftLoading(false)
    }
  }

  useEffect(() => {
    if (!id) return
    previewDeclaration(id).then(result => {
      if (result.success) {
        setPreview(result)
        setSourcePvaRows(cloneRows(result.variants.pva.rows, result.itemIds))
        setSourceNonPvaRows(cloneRows(result.variants.nonPva.rows, result.itemIds))
        setPvaRows(cloneRows(result.variants.pva.rows, result.itemIds))
        setNonPvaRows(cloneRows(result.variants.nonPva.rows, result.itemIds))
        if (result.job.importer_id) {
          findPreviousBatch(result.job.importer_id, id).then(setPreviousBatch)
        }
      } else {
        setPreviewError(result.error || 'Failed to build declaration preview')
      }
    })
    loadHistory()
  }, [id, previewDeclaration, loadHistory, findPreviousBatch])

  // Z73.6 revised: a "warning"-severity item (POA expiring soon, borderline name match) never
  // blocks the grid/generate/submit -- only an unpassed item with no severity or "error" does.
  const failedChecks = preview?.checklist.filter(c => !c.passed && c.severity !== 'warning') || []
  const warningChecks = preview?.checklist.filter(c => !c.passed && c.severity === 'warning') || []
  const canSubmit = !!preview && failedChecks.length === 0

  const activeRows = tab === 'pva' ? pvaRows : nonPvaRows
  const activeSourceRows = tab === 'pva' ? sourcePvaRows : sourceNonPvaRows
  const setActiveRows = tab === 'pva' ? setPvaRows : setNonPvaRows

  const summary = useMemo(() => {
    const currency = activeRows.find(r => r.cells['VALUE CURR'])?.cells['VALUE CURR'] || ''
    return {
      lines: activeRows.length,
      totalValue: sumField(activeRows, 'VALUE'),
      currency,
      totalCartons: sumField(activeRows, 'PKG QTY'),
      totalWeight: sumField(activeRows, 'GROSS MASS'),
    }
  }, [activeRows])

  // Gary's ">10% value change" warning -- compared against this variant's own as-generated total,
  // not the other variant's (PVA/non-PVA totals are meant to match each other, but each is
  // compared to where IT started).
  const valueChangeWarning = useMemo(() => {
    const sourceTotal = sumField(activeSourceRows, 'VALUE')
    if (sourceTotal <= 0) return null
    const editedTotal = sumField(activeRows, 'VALUE')
    const pct = Math.abs(editedTotal - sourceTotal) / sourceTotal
    if (pct <= 0.10) return null
    return `Edited total value (${editedTotal.toFixed(2)}) differs from the source total (${sourceTotal.toFixed(2)}) by ${(pct * 100).toFixed(0)}% -- double-check before confirming.`
  }, [activeRows, activeSourceRows])

  function handleCellChange(rowIdx: number, field: string, value: string) {
    setActiveRows(prev => prev.map((r, i) => (i === rowIdx ? { ...r, cells: { ...r.cells, [field]: value } } : r)))
  }
  function handleResetCell(rowIdx: number, field: string) {
    const source = activeSourceRows[rowIdx]?.cells[field] ?? ''
    handleCellChange(rowIdx, field, source)
  }
  function handleRemoveRow(rowIdx: number) {
    setActiveRows(prev => prev.filter((_, i) => i !== rowIdx))
  }
  function handleAddRow() {
    if (!preview) return
    setActiveRows(prev => [...prev, emptyRow(preview.headers)])
  }
  // Z73.11 per Gary: "if a column is the same for all lines, one-click to fill all".
  function handleSetAllRows(field: string, value: string) {
    setActiveRows(prev => prev.map(r => ({ ...r, cells: { ...r.cells, [field]: value } })))
  }

  // Z73.11: the bottom "N lines / X blocker cells / Y warning cells" counter -- blocker mirrors
  // the same REQUIRED_FIELDS the grid already reddens; warning mirrors RECOMMENDED_FIELDS (the
  // grid's lighter amber). Counts cells, not rows -- 2 missing fields on 1 row is 2, not 1.
  const cellCounts = useMemo(() => {
    let blockers = 0
    let warnings = 0
    for (const row of activeRows) {
      for (const field of REQUIRED_FIELDS) if (!row.cells[field]?.trim()) blockers++
      for (const field of RECOMMENDED_FIELDS) if (!row.cells[field]?.trim()) warnings++
    }
    return { blockers, warnings }
  }, [activeRows])

  function handleCopyFromPrevious() {
    if (!previousBatch) return
    if (!confirm(`Copy the declaration from job ${previousBatch.jobRef}? This replaces your current edits in both tabs.`)) return
    setPvaRows(cloneRows(previousBatch.pvaRows, []))
    setNonPvaRows(cloneRows(previousBatch.nonPvaRows, []))
    setGenerateSucceeded(false)
    setGenerateMessage(null)
  }

  // Z73.7: always forces the 'uat' environment -- this button is labeled "Submit to Descartes
  // UAT" and there is no PRODUCTION submit wired up yet, so it must never depend on whatever
  // ECUSTOMS_ENV happens to be configured as server-side. Only reachable after a successful
  // Confirm & Generate (see the button's disabled condition below) -- submits exactly what was
  // just confirmed, not a stale unedited preview.
  // Z92: step 1 -- make sure a file exists for the CURRENT VAT-treatment selection, then ask for a final
  // review. A file built for a different selection is never reused (the button is disabled; see
  // fileStale), and the server re-checks the file's DT rows against the selection anyway.
  async function handleSubmitClick() {
    if (!id || !preview || !checklistOk) return
    setSubmitMessages(null)
    setSubmitSucceeded(false)
    if (!v2Result?.submissionId || !v2Result.csvContent || !v2Result.filename) {
      let file: EcustomsGenerateV2Result
      try {
        file = await generateDeclarationV2(id, decisions)
      } catch (e) {
        const msg = `Failed to generate file: ${e instanceof Error ? e.message : String(e)}`
        setSubmitMessages({ pva: msg, nonPva: msg })
        return
      }
      if (!file.success || !file.submissionId || !file.csvContent || !file.filename || file.pvaMode !== pvaMode || file.danMode !== danMode) {
        const msg = file.error || 'Failed to generate file'
        setSubmitMessages({ pva: msg, nonPva: msg })
        return
      }
      setV2Result(file)
    }
    setConfirmOpen(true)
  }

  // Step 2 -- after the reviewer's tick. Sends exactly the file just generated, together with the
  // selection the reviewer saw; the server refuses if they disagree.
  async function handleConfirmedSubmit() {
    if (!id || !pvaMode || !danMode || fileStale || !v2Result?.submissionId || !v2Result.filename) return
    const file = v2Result
    const result = await submitDeclarationV2(id, file.submissionId!, file.filename!, pvaMode, danMode)
    setConfirmOpen(false)
    const msg = result.success
      ? `Sent to Descartes UAT (HTTP ${result.httpStatus}) as ${result.pvaMode === 'PVA' ? 'PVA' : 'Non-PVA'}. Raw response saved on submission ${result.submissionId} -- ${file.filename}`
      : `Not sent / failed: ${result.error || 'no detail'}${result.httpStatus ? ` (HTTP ${result.httpStatus})` : ''}`
    setSubmitMessages({ pva: msg, nonPva: msg })
    setSubmitSucceeded(result.success)
    setV2Result(null) // this file is consumed -- the next submit generates a fresh sequence number
    await loadHistory()
  }

  // Z73.7 / Z73.8: Step 5/6/7 -- re-validates server-side, builds the final CSVs from exactly
  // these edited rows, downloads them to Gary's machine, and writes the audit trail.
  async function handleGenerate() {
    if (!id || !preview) return
    setGenerateError(null)
    setRowErrors(null)
    setGenerateMessage(null)
    const itemIdsForSaveBack = (pvaRows.length > 0 ? pvaRows : nonPvaRows).map(r => r.itemIds)
    const payload = {
      headers: preview.headers,
      pvaRows: pvaRows.map(r => r.cells),
      nonPvaRows: nonPvaRows.map(r => r.cells),
      itemIds: itemIdsForSaveBack,
      overrideScope,
    }
    console.log('[eCustoms] handleGenerate: calling generateDeclaration', { jobId: id, pvaRowCount: payload.pvaRows.length, nonPvaRowCount: payload.nonPvaRows.length, overrideScope })

    let result: Awaited<ReturnType<typeof generateDeclaration>>
    try {
      result = await generateDeclaration(id, payload)
    } catch (e) {
      console.error('[eCustoms] handleGenerate: generateDeclaration threw', e)
      setGenerateError(e instanceof Error ? e.message : 'Failed to generate files (request error -- see console)')
      return
    }
    console.log('[eCustoms] handleGenerate: response received', result)

    if (!result.success) {
      console.error('[eCustoms] handleGenerate: server reported failure', result.error, result.rowErrors)
      setGenerateError(result.error || 'Failed to generate files')
      setRowErrors(result.rowErrors || null)
      return
    }

    // Z73.8 Bug 3: a stale-deployed edge function can return success:true from an entirely
    // different (older) response shape with no pva/nonPva.csvContent at all -- treat "succeeded
    // but produced nothing to download" as an error instead of a false-positive success toast.
    if (!result.pva?.csvContent && !result.nonPva?.csvContent) {
      console.error('[eCustoms] handleGenerate: success=true but no csvContent on either variant -- likely a stale-deployed edge function. Full response:', result)
      setGenerateError('Server reported success but returned no file content for either variant -- the eCustoms edge function may need redeploying. See browser console for the full response.')
      return
    }

    const jobRef = preview.job.job_ref || id
    let downloadCount = 0
    if (result.pva?.csvContent) { downloadCsv(`${jobRef}_CDS_PVA.csv`, result.pva.csvContent); downloadCount++ }
    if (result.nonPva?.csvContent) { downloadCsv(`${jobRef}_CDS_NonPVA.csv`, result.nonPva.csvContent); downloadCount++ }
    console.log(`[eCustoms] handleGenerate: ${downloadCount} file(s) downloaded`)
    setGenerateSucceeded(true)
    let message = 'Files generated and downloaded -- upload to Descartes UAT manually, then let Gary know once confirmed.'
    if (result.savedToSource) {
      message += ` Saved ${result.savedToSource.updated} row(s) back to the source CI line items.`
      if (result.savedToSource.skipped.length > 0) {
        message += ` ${result.savedToSource.skipped.length} row(s) couldn't be saved back (spans multiple/no source lines) -- kept as submission-only for those.`
      }
    }
    setGenerateMessage(message)
    await loadHistory()
  }

  // Z73.18 Phase 2: download-only verification for the new v1.8.3 multi-row-type format --
  // never touches Descartes, just gives Gary a file to compare against a real UAT response
  // before Phase 3 wires up a live submit for this format.
  async function handleGenerateV2() {
    if (!id) return
    if (!checklistOk) { setV2Error(checklistHint + '.'); return }
    setV2Error(null)
    let result: Awaited<ReturnType<typeof generateDeclarationV2>>
    try {
      result = await generateDeclarationV2(id, decisions)
    } catch (e) {
      const err = e instanceof Error ? e : new Error(String(e))
      console.error('[eCustoms] v1.8.3 generation failed:', err, err.stack)
      Sentry.captureException(err, { tags: { feature: 'ecustoms_v183_generate' }, extra: { jobId: id } })
      setV2Error(`Failed to generate v1.8.3 file: ${err.message}`)
      return
    }
    if (!result.success || !result.csvContent) {
      // success:true with no csvContent (rather than success:false) means the server didn't
      // recognize action='generate_v2' at all and silently fell through to the preview response
      // shape instead -- almost always a stale-deployed edge function, not a real generation
      // failure, so say so rather than showing the generic message for both cases.
      const message = result.error
        || (result.success ? 'Server returned no file content -- the deployed edge function may not have the v1.8.3 generator yet' : 'Failed to generate v1.8.3 file')
      console.error('[eCustoms] v1.8.3 generation returned no file:', result)
      Sentry.captureException(new Error(`generate_v2 failed: ${message}`), {
        tags: { feature: 'ecustoms_v183_generate' }, extra: { jobId: id, result },
      })
      setV2Error(message)
      return
    }
    setV2Result(result)
    downloadCsv(result.filename || `${preview?.job.job_ref || id}_CDS_v1.8.3.csv`, result.csvContent)
    if (result.soapXml) downloadCsv((result.filename || 'declaration') + '_SOAP.xml', result.soapXml)
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <div className="container mx-auto px-4 py-8 max-w-6xl">
        <div className="mb-6">
          {id && <Link to={`/jobs/${id}`} className="text-sm text-blue-600 hover:underline">&larr; Back to Job</Link>}
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white mt-2">eCustoms Declaration Preview</h1>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            Every job produces both a PVA and a non-PVA CDS Level 2 CSV -- different line items or
            circumstances may need different VAT treatment. PVA (Postponed VAT Accounting) defers
            the importer's VAT to their VAT return; non-PVA pays VAT at the point of import.
            Review and edit the line items below, then Confirm & Generate Files -- nothing is
            downloaded or submitted before that.
          </p>
        </div>

        {previewLoading && !preview && <p className="text-gray-500">Building preview…</p>}

        {previewError && (
          <div className="mb-4 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg text-sm text-red-700 dark:text-red-400">
            {previewError}
          </div>
        )}

        {preview && (
          <>
            {!preview.hasCiData && (
              <div className="mb-6 p-4 bg-red-50 dark:bg-red-900/20 border border-red-300 dark:border-red-800 rounded-lg text-sm text-red-800 dark:text-red-300">
                <p className="font-semibold">⚠ No Commercial Invoice data found for {preview.job.job_ref}.</p>
                <p className="mt-1">Please upload CI in Commercial Invoice Management first (Job Ref field must be set to {preview.job.job_ref}), then return here.</p>
                <Link
                  to={`/customs/invoices/upload?job_ref=${encodeURIComponent(preview.job.job_ref)}`}
                  className="inline-block mt-2 font-medium text-red-700 dark:text-red-300 hover:underline"
                >
                  Upload CI now →
                </Link>
              </div>
            )}

            <ChecklistPanel checklist={preview.checklist} />

            {preview.poaDriveLink && (
              <div className="mb-6 -mt-4 text-sm">
                <a href={preview.poaDriveLink} target="_blank" rel="noreferrer" className="text-blue-600 dark:text-blue-400 hover:underline">
                  View current POA document →
                </a>
              </div>
            )}

            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6 mb-6">
              <h2 className="text-base font-semibold text-gray-900 dark:text-white mb-3">Job Summary ({preview.environment.toUpperCase()} environment)</h2>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm">
                <div>
                  <p className="text-gray-500 dark:text-gray-400 text-xs uppercase tracking-wide">Job Ref</p>
                  <p className="text-gray-900 dark:text-white font-medium mt-1">{preview.job.job_ref || <span className="text-red-500">Missing</span>}</p>
                </div>
                <div>
                  <p className="text-gray-500 dark:text-gray-400 text-xs uppercase tracking-wide">Shipper / Consignor</p>
                  <p className="text-gray-900 dark:text-white font-medium mt-1">{preview.job.consignor_name || <span className="text-red-500">Missing</span>}</p>
                </div>
                <div>
                  {/* Z73.8 Bug 2: badge shown right next to the importer name, per Gary's exact
                      ask -- the checklist below also carries the full "expires on..." detail. */}
                  <p className="text-gray-500 dark:text-gray-400 text-xs uppercase tracking-wide">Consignee / Importer</p>
                  <p className="text-gray-900 dark:text-white font-medium mt-1 flex items-center gap-2 flex-wrap">
                    {preview.job.importer_name || <span className="text-red-500">Missing</span>}
                    <PoaStatusBadge badge={preview.poaBadge} title="POA validity for this importer's EORI -- see Validation Checklist below for full detail" />
                  </p>
                </div>
                {[
                  ['Origin Port LOCODE', preview.job.origin_port_locode],
                  ['Destination Port LOCODE', preview.job.destination_port_locode],
                  ['AWB / BL', preview.job.awb_or_bl],
                ].map(([label, val]) => (
                  <div key={label as string}>
                    <p className="text-gray-500 dark:text-gray-400 text-xs uppercase tracking-wide">{label}</p>
                    <p className="text-gray-900 dark:text-white font-medium mt-1">{val || <span className="text-red-500">Missing</span>}</p>
                  </div>
                ))}
                <div>
                  <p className="text-gray-500 dark:text-gray-400 text-xs uppercase tracking-wide">Importer EORI</p>
                  {preview.job.importer_eori ? (
                    <p className="text-gray-900 dark:text-white font-medium mt-1">{preview.job.importer_eori}</p>
                  ) : (
                    <p className="mt-1">
                      <span className="text-red-500 font-medium">Missing</span>
                      {preview.job.importer_id && (
                        <Link to={`/customers/${preview.job.importer_id}`} className="ml-2 text-xs text-blue-600 hover:underline">
                          Edit customer to add EORI →
                        </Link>
                      )}
                    </p>
                  )}
                </div>
              </div>
            </div>

            {/* Z73.5 per Gary: EORI presence alone determines PVA eligibility -- always shown
                (not just when N/A), so staff see the positive confirmation too. */}
            <div className="mb-6 p-4 bg-gray-50 dark:bg-gray-800/50 border border-gray-200 dark:border-gray-700 rounded-lg text-sm text-gray-600 dark:text-gray-400">
              {preview.variants.pva.applicable ? (
                <p>PVA available for this importer (EORI: {preview.job.importer_eori}).</p>
              ) : (
                <div>
                  <p>PVA: N/A for this job (importer EORI missing).</p>
                  <p className="text-xs mt-0.5">
                    Add an EORI number on the importer's customer record if PVA should apply.
                    {preview.job.importer_id && (
                      <Link to={`/customers/${preview.job.importer_id}`} className="ml-1 text-blue-600 hover:underline">Edit customer →</Link>
                    )}
                  </p>
                </div>
              )}
              {!preview.variants.nonPva.applicable && <p>Non-PVA: N/A for this job.</p>}
            </div>

            {failedChecks.length > 0 ? (
              <div className="mb-6 p-4 bg-red-50 dark:bg-red-900/20 border border-red-300 dark:border-red-800 rounded-lg text-sm">
                <p className="text-red-700 dark:text-red-400 font-medium mb-1">
                  ❌ Resolve the validation checklist above before editing the declaration ({failedChecks.length} issue{failedChecks.length === 1 ? '' : 's'}).
                </p>
              </div>
            ) : (
              <>
                {warningChecks.length > 0 && (
                  <div className="mb-4 p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-300 dark:border-amber-800 rounded-lg text-sm text-amber-800 dark:text-amber-300">
                    {warningChecks.map(c => <p key={c.key}>⚠ {c.detail || c.label}</p>)}
                  </div>
                )}
                {previousBatch && (
                  <div className="mb-4 flex items-center justify-between p-3 bg-indigo-50 dark:bg-indigo-900/20 border border-indigo-200 dark:border-indigo-800 rounded-lg text-sm">
                    <p className="text-indigo-800 dark:text-indigo-300">A similar recent declaration exists for job <strong>{previousBatch.jobRef}</strong> (same importer).</p>
                    <button onClick={handleCopyFromPrevious} className="px-3 py-1 bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-medium rounded-lg">
                      Copy from previous submission
                    </button>
                  </div>
                )}

                <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden mb-6">
                  <div className="flex border-b border-gray-200 dark:border-gray-700">
                    <button
                      onClick={() => setTab('pva')}
                      className={`px-4 py-3 text-sm font-medium ${tab === 'pva' ? 'border-b-2 border-blue-600 text-blue-600 dark:text-blue-400' : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'}`}
                    >
                      With PVA
                    </button>
                    <button
                      onClick={() => setTab('nonPva')}
                      className={`px-4 py-3 text-sm font-medium ${tab === 'nonPva' ? 'border-b-2 border-blue-600 text-blue-600 dark:text-blue-400' : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'}`}
                    >
                      Without PVA
                    </button>
                    <button
                      onClick={() => setTab('rawCsv')}
                      className={`px-4 py-3 text-sm font-medium ${tab === 'rawCsv' ? 'border-b-2 border-blue-600 text-blue-600 dark:text-blue-400' : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'}`}
                      title="v1.8.3 multi-row-type format -- inspect and edit every field before downloading, instead of counting commas in the raw CSV"
                    >
                      Raw CSV Editor
                    </button>
                    {tab !== 'rawCsv' && (
                      <div className="ml-auto px-4 py-3 text-xs text-gray-500 dark:text-gray-400 flex items-center gap-4">
                        <span>{summary.lines} line{summary.lines === 1 ? '' : 's'}</span>
                        <span>{summary.currency} {summary.totalValue.toFixed(2)} total value</span>
                        <span>{summary.totalCartons} cartons</span>
                        <span>{summary.totalWeight.toFixed(1)} kg</span>
                      </div>
                    )}
                  </div>

                  {tab === 'rawCsv' ? (
                    <div className="p-4">
                      {!v2Result?.structuredRows ? (
                        <div className="text-center py-8">
                          <p className="text-sm text-gray-500 dark:text-gray-400 mb-3">
                            Generate the v1.8.3 file first to inspect and edit its fields here.
                          </p>
                          <button
                            onClick={handleGenerateV2}
                            disabled={generatingV2 || !checklistOk}
                            title={!checklistOk ? checklistHint : undefined}
                            className="px-4 py-2 bg-orange-600 hover:bg-orange-700 text-white rounded-lg text-sm font-medium disabled:opacity-50"
                          >
                            {generatingV2 ? 'Generating…' : 'Generate v1.8.3 File'}
                          </button>
                          {v2Error && <p className="mt-2 text-xs text-red-600 dark:text-red-400">{v2Error}</p>}
                        </div>
                      ) : (
                        <RawCsvEditor
                          structuredRows={v2Result.structuredRows}
                          filename={v2Result.filename}
                          onRegenerate={handleGenerateV2}
                          regenerating={generatingV2}
                        />
                      )}
                    </div>
                  ) : (
                    <>
                      {valueChangeWarning && (
                        <div className="px-4 py-2 bg-amber-50 dark:bg-amber-900/20 border-b border-amber-200 dark:border-amber-800 text-xs text-amber-800 dark:text-amber-300">
                          ⚠ {valueChangeWarning}
                        </div>
                      )}

                      <EditableGrid
                        headers={preview.headers}
                        rows={activeRows}
                        sourceRows={activeSourceRows}
                        onCellChange={handleCellChange}
                        onResetCell={handleResetCell}
                        onRemoveRow={handleRemoveRow}
                        onSetAllRows={handleSetAllRows}
                      />

                      <div className="px-4 py-2 border-t border-gray-200 dark:border-gray-700 flex items-center justify-between">
                        <button onClick={handleAddRow} className="text-sm text-blue-600 dark:text-blue-400 hover:underline">+ Add Row</button>
                        <div className="text-xs text-gray-500 dark:text-gray-400 flex items-center gap-3">
                          <span>{activeRows.length} line{activeRows.length === 1 ? '' : 's'}</span>
                          <span className={cellCounts.blockers > 0 ? 'text-red-600 dark:text-red-400 font-medium' : ''}>
                            {cellCounts.blockers} blocker cell{cellCounts.blockers === 1 ? '' : 's'} to fill
                          </span>
                          <span className={cellCounts.warnings > 0 ? 'text-amber-600 dark:text-amber-400 font-medium' : ''}>
                            {cellCounts.warnings} warning cell{cellCounts.warnings === 1 ? '' : 's'}
                          </span>
                        </div>
                      </div>
                    </>
                  )}
                </div>

                {tab !== 'rawCsv' && (
                  <>
                    <ColumnLegendPanel headers={preview.headers} />

                    <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-4 mb-6">
                      <p className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">If you edited any rows, should the changes be saved?</p>
                      <div className="space-y-1 text-sm">
                        <label className="flex items-center gap-2 cursor-pointer">
                          <input type="radio" checked={overrideScope === 'submission_only'} onChange={() => setOverrideScope('submission_only')} />
                          <span className="text-gray-700 dark:text-gray-300">Use for this submission only -- don't change the source Commercial Invoice</span>
                        </label>
                        <label className="flex items-center gap-2 cursor-pointer">
                          <input type="radio" checked={overrideScope === 'saved_to_source'} onChange={() => setOverrideScope('saved_to_source')} />
                          <span className="text-gray-700 dark:text-gray-300">Save these edits back to the source CI line items too (only possible for a row that maps to exactly one CI line)</span>
                        </label>
                      </div>
                    </div>
                  </>
                )}

                {rowErrors && rowErrors.length > 0 && (
                  <div className="mb-4 p-4 bg-red-50 dark:bg-red-900/20 border border-red-300 dark:border-red-800 rounded-lg text-sm text-red-700 dark:text-red-400">
                    <p className="font-semibold mb-1">{rowErrors.length} required field{rowErrors.length === 1 ? '' : 's'} missing:</p>
                    <ul className="list-disc list-inside">
                      {rowErrors.slice(0, 10).map((e, i) => (
                        <li key={i}>{e.variant === 'pva' ? 'With PVA' : 'Without PVA'}, row {e.row + 1}: {e.field}</li>
                      ))}
                      {rowErrors.length > 10 && <li>…and {rowErrors.length - 10} more</li>}
                    </ul>
                  </div>
                )}
                {generateError && !rowErrors && (
                  <div className="mb-4 p-4 bg-red-50 dark:bg-red-900/20 border border-red-300 dark:border-red-800 rounded-lg text-sm text-red-700 dark:text-red-400">
                    {generateError}
                  </div>
                )}

                <div className="flex flex-col gap-3 mb-6">
                  <PreSubmitChecklist
                    transportMode={preview.job.transport_mode ?? null}
                    importerName={preview.job.importer_name}
                    importerPvaFlag={preview.job.importer_pva_flag ?? null}
                    danDefault={preview.job.dan_default ?? null}
                    danDefaultSource={preview.job.dan_default_source ?? null}
                    decisions={decisions}
                    danOverridden={danInput != null}
                    repOverridden={repInput != null}
                    loadOverridden={loadInput != null}
                    precheck={precheck}
                    loading={precheckLoading}
                    ticks={ticks}
                    onTick={(tid, value, checked) => setTicks(t => { const n = { ...t }; if (checked) n[tid] = value; else delete n[tid]; return n })}
                    set={{ pva: setPvaMode, dan: setDanInput, declType: setDeclType, rep: setRepInput, load: setLoadInput, arrival: setArrivalIdInput, mrn: setMrnInput, vatOverride: setVatOverride }}
                  />
                  {fileStale && (
                    <div className="rounded-lg border border-red-400 bg-red-50 p-3 text-sm text-red-800 dark:border-red-700 dark:bg-red-900/20 dark:text-red-200">
                      <p className="font-semibold">Setting changed -- please regenerate the file.</p>
                      <p>The file on screen was generated from different checklist choices than the ones now selected (VAT treatment, DAN, declaration type, representation, load type, VAT adjustment, arrival ID or Transit MRN). Submit is disabled until the file is regenerated.</p>
                      <button onClick={handleGenerateV2} disabled={generatingV2} className="mt-2 rounded-lg border border-red-500 px-3 py-1 text-xs font-medium disabled:opacity-50">
                        {generatingV2 ? 'Regenerating…' : 'Regenerate file now'}
                      </button>
                    </div>
                  )}
                  <div className="flex flex-wrap items-center gap-3">
                    <button
                      onClick={handleGenerate}
                      disabled={generating}
                      className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                    >
                      {generating && (
                        <svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                        </svg>
                      )}
                      {generating ? 'Generating…' : generateSucceeded ? 'Files Generated ✓ -- Regenerate' : 'Confirm & Generate Files'}
                    </button>

                    <span className="w-px self-stretch bg-gray-200 dark:bg-gray-700 mx-1" />

                    <button
                      onClick={handleSubmitClick}
                      disabled={submitting || generatingV2 || !canSubmit || !uatVerified || !checklistOk || fileStale}
                      title={!checklistOk ? checklistHint : fileStale ? 'The VAT treatment changed -- regenerate the file first' : !canSubmit ? 'Fix the failed validation checks first' : !uatVerified ? 'Enable the UAT toggle in Admin -> eCustoms Settings' : 'Generates the v1.8.3 file, then asks you to review before sending to Descartes UAT'}
                      className="px-3 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                    >
                      {submitting && (
                        <svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                        </svg>
                      )}
                      {submitting ? 'Submitting…' : submitSucceeded ? 'Submitted ✓' : 'Submit to Descartes UAT'}
                    </button>

                    {uatVerified && (
                      <button
                        disabled
                        title="Production submit not yet enabled -- a separate go-ahead is needed once UAT is confirmed working end to end"
                        className="px-3 py-2 border border-gray-300 dark:border-gray-600 text-gray-400 dark:text-gray-500 rounded-lg text-sm font-medium opacity-50 cursor-not-allowed"
                      >
                        Submit to Descartes PRODUCTION
                      </button>
                    )}
                  </div>

                  {generateMessage && <p className="text-sm text-green-700 dark:text-green-400">{generateMessage}</p>}

                  {/* Z73.18 Phase 2: separate, download-only path for the new v1.8.3 multi-row-type
                      format -- not yet wired to a live submit, just gives Gary a file to verify
                      against Descartes UAT by hand before Phase 3 turns it into a real button. */}
                  <div className="mt-1 p-3 border border-dashed border-orange-300 dark:border-orange-800 rounded-lg bg-orange-50/50 dark:bg-orange-900/10">
                    <p className="text-xs text-orange-800 dark:text-orange-300 mb-2">
                      New multi-row-type format (v1.8.3) -- verification only, not yet submitted anywhere.
                    </p>
                    <button
                      onClick={handleGenerateV2}
                      disabled={generatingV2 || !checklistOk}
                      title={!checklistOk ? checklistHint : undefined}
                      className="px-3 py-1.5 border border-orange-400 dark:border-orange-700 text-orange-700 dark:text-orange-300 rounded-lg hover:bg-orange-100 dark:hover:bg-orange-900/30 text-xs font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {generatingV2 ? 'Generating…' : 'Generate v1.8.3 File (for UAT comparison)'}
                    </button>
                    {v2Error && <p className="mt-2 text-xs text-red-600 dark:text-red-400">{v2Error}</p>}
                  </div>

                  {confirmOpen && pvaMode && v2Result?.filename && (
                    <SubmitConfirmDialog
                      jobRef={preview.job.job_ref}
                      importerName={preview.job.importer_name}
                      pvaMode={pvaMode}
                      decisions={v2Result.decisions ?? null}
                      review={v2Result.review ?? []}
                      checks={v2Result.checks ?? []}
                      vatAdjustment={v2Result.vatAdjustment ?? null}
                      filename={v2Result.filename}
                      environment="UAT"
                      submitting={submitting}
                      onConfirm={handleConfirmedSubmit}
                      onCancel={() => setConfirmOpen(false)}
                    />
                  )}

                  {submitMessages && (
                    <div className="text-sm space-y-0.5">
                      <p><span className="font-medium">PVA:</span> {submitMessages.pva}</p>
                      <p><span className="font-medium">Non-PVA:</span> {submitMessages.nonPva}</p>
                    </div>
                  )}

                  {/* Z73.16 Part 4 */}
                  {latestGeneratedBatch && (
                    <div className="mt-2 p-4 border border-gray-200 dark:border-gray-700 rounded-lg bg-gray-50 dark:bg-gray-900/30">
                      <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">📋 Invoice from this declaration</p>
                      {latestGeneratedBatch.invoiceId ? (
                        <button
                          onClick={() => navigate(`/accounts?invoice=${latestGeneratedBatch.invoiceId}`)}
                          className="text-sm text-blue-600 dark:text-blue-400 hover:underline"
                        >
                          → View Invoice {latestGeneratedBatch.invoiceNumber || ''}
                        </button>
                      ) : (
                        <>
                          <button
                            onClick={handleCreateInvoiceDraft}
                            disabled={invoiceDraftLoading}
                            className="px-3 py-1.5 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            {invoiceDraftLoading ? 'Creating…' : 'Create Invoice Draft'}
                          </button>
                          {invoiceDraftError && <p className="text-sm text-red-600 dark:text-red-400 mt-2">{invoiceDraftError}</p>}
                        </>
                      )}
                    </div>
                  )}
                </div>
              </>
            )}
          </>
        )}

        {historyError && <p className="rounded border border-red-300 bg-red-50 p-3 text-sm text-red-800">{historyError}</p>}
        {history.length > 0 && (
          <SubmissionHistory
            jobRef={preview?.job.job_ref ?? ''}
            submissions={history}
            getArchive={getSubmissionArchive}
            statusLabel={formatSubmissionStatus}
          />
        )}
      </div>
    </div>
  )
}
